import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';
import { ApolloError } from '@apollo/client';
import { GiftWrapType } from '@apps/registry/guest/GuestRegistry.telemetry';
import { AccommodationStepPushCohort } from './hooks/useAccommodationStepPushCohort';
import { EventAccommodationData } from '@shared/hooks/useEventAccommodationsCheck';

const category = 'rsvp.giftwrap';

export const thanksForRSVPTelemetry = createTelemetryObject({
  pages: {
    accommodationsPushStep: (cohort: AccommodationStepPushCohort) => ({
      category: 'rsvp',
      page: 'accommodationsPushStep',
      pagePrefix: 'guest',
      extraPageInfo: {
        cohort,
      },
    }),
  },
  actions: {
    trackError: (error: ApolloError | Error | unknown) => ({
      category,
      action: 'Mutation',
      actionType: 'error',
      extraInfo: { error },
    }),
    giftWrapMessageEdited: ({ originalNote, modifiedNote }: { originalNote: string; modifiedNote: string }) => ({
      category: 'guest.rsvp.giftwrap',
      action: 'AIDraftEdited',
      label: 'giftwrap',
      extraInfo: {
        originalNote,
        modifiedNote,
      },
    }),
    giftWrapPromptSent: ({ prompt }: { prompt: string }) => ({
      category: 'guest.rsvp.giftwrap',
      action: 'PromptSentToAI',
      label: 'giftwrap',
      extraInfo: {
        prompt,
      },
    }),
    giftWrapOfferViewed: ({ eventHandle, type, emailFieldDisplayed }: { eventHandle: string; type: GiftWrapType; emailFieldDisplayed: boolean }) => ({
      category,
      action: 'GiftWrapOfferViewed',
      label: eventHandle,
      extraInfo: {
        checkoutType: type,
        emailFieldDisplayed,
      },
    }),
    basicCongratsOfferViewed: ({ eventHandle }: { eventHandle: string }) => ({
      category: 'guest.rsvp.giftwrap',
      action: 'BasicCongratsOfferViewed',
      label: eventHandle,
    }),
    rsvpSentPageViewed: ({
      href,
      fromGiftWrap,
      hasRoomBlocksBookingUrl,
      hasRegistry,
      hasAccommodations,
    }: {
      href: string;
      fromGiftWrap: boolean;
      hasRoomBlocksBookingUrl: boolean;
      hasRegistry: boolean;
      hasAccommodations?: boolean;
    }) => ({
      action: 'PageViewed',
      category: 'guest.rsvp',
      extraInfo: {
        name: fromGiftWrap ? 'rsvpAndCardSent' : 'rsvpSent',
        url: href,
        hasRoomBlocksBookingUrl,
        hasRegistry,
        hasAccommodations,
      },
    }),
    sendCongratulationsCardClicked: ({ design, message, price, rsvpStatus }: { design: string; message: string; price: string; rsvpStatus: string }) => ({
      category,
      action: 'AddGiftWrap',
      label: design,
      actionType: 'click',
      extraInfo: {
        design,
        message,
        price,
        rsvpStatus,
      },
    }),
    sendCongratulationsNoteClicked: ({ message, rsvpStatus }: { message: string; rsvpStatus: string }) => ({
      category,
      action: 'AddGiftWrap',
      label: 'basic',
      actionType: 'click',
      extraInfo: {
        message,
        rsvpStatus,
      },
    }),
    shopAccommodationClicked: ({ isGWScreen }: { isGWScreen: boolean }) => ({
      category: isGWScreen ? 'guest.rsvp.giftwrap' : 'guest.rsvp',
      action: 'ButtonInteracted',
      label: 'ViewAccommodations',
      actionType: 'click',
    }),
    shopRegistryClicked: ({ isGWScreen }: { isGWScreen: boolean }) => ({
      category: isGWScreen ? 'guest.rsvp.giftwrap' : 'guest.rsvp',
      action: 'ButtonInteracted',
      label: 'ShopRegistry',
      actionType: 'click',
    }),
    backToWebsiteClicked: ({ isGWScreen }: { isGWScreen: boolean }) => ({
      category: isGWScreen ? 'guest.rsvp.giftwrap' : 'guest.rsvp',
      action: 'ButtonInteracted',
      label: 'BacktoWebsite',
      actionType: 'click',
    }),
    previewYourNoteClicked: () => ({
      category: 'guest.rsvp.giftwrap',
      action: 'ButtonInteracted',
      label: 'PreviewYourNote',
      actionType: 'click',
    }),
    giftWrapEditMessageClicked: ({ label }: { label: string }) => ({
      category: 'guest.rsvp.giftwrap',
      action: 'ButtonInteracted',
      label,
    }),
    uneditedWarningShown: () => ({
      category,
      action: 'giftwrapUneditedWarning',
    }),
    transactionCompleted: ({ salesOrderId, price }: { salesOrderId: string; price: string }) => ({
      category: 'guest.rsvp.giftwrap',
      action: 'RsvpCongratsTransactionCompleted',
      label: 'rsvp',
      extraInfo: {
        salesOrderId,
        hasGiftWrap: true,
        price,
      },
    }),
    accommodationPushStepButtonClick: (args: {
      cta: 'viewHotelPdp' | 'exploreAllHotels' | 'skip';
      totalAccommodationCount?: number;
      targetAccommodationListingPosition?: number;
      cohort: AccommodationStepPushCohort;
      accommodationType?: EventAccommodationData['type'];
      queryParams?: Record<string, string>;
    }) => ({
      category: 'guest.rsvp',
      action: 'TravelButtonInteracted',
      extraInfo: {
        cohort: args.cohort,
        totalAccommodationCount: args.totalAccommodationCount,
        targetAccommodationListingPosition: args.targetAccommodationListingPosition,
        ...(args.queryParams ?? {}),
        name: `accommodationsPushStep_${args.cta}Cta`,
        // Explicitly setting it here so that it doesn't override the original `name` telemetry field
        locationName: args.queryParams?.name,
        accommodationType: args.accommodationType,
      },
    }),
    postRsvpViewNearbyHotelsClickedMessaging: ({ destinationURL, queryParams }: { destinationURL: string; queryParams: Record<string, string> }) => ({
      category: 'guest.rsvp',
      action: 'ButtonInteracted',
      actionType: 'click',
      label: 'SearchHotel',
      extraInfo: {
        destinationURL,
        ...queryParams,
      },
    }),
    postRsvpViewNearbyHotelsClickedTravel: ({ destinationURL, queryParams }: { destinationURL: string; queryParams: Record<string, string> }) => ({
      category: 'guest.rsvp',
      action: 'TravelButtonInteracted',
      label: 'SearchHotel',
      extraInfo: {
        destinationURL,
        ...queryParams,
      },
    }),
  },
});

const { TelemetryProvider, useTelemetry } = createTelemetry(thanksForRSVPTelemetry);
const useThanksForRSVPTelemetry = useTelemetry;
export { TelemetryProvider, useThanksForRSVPTelemetry };
